import { Check, Pencil, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * A heading that can be renamed in place.
 *
 * The title reads as a title until the pencil is pressed (or the heading is
 * tapped on a touch screen); then it is an input with the text selected,
 * saved on Enter or blur and abandoned on Escape. Nothing else on the page
 * moves — a rename that reflows the screen makes people doubt what they
 * renamed.
 */
export function InlineTitle({
  value,
  onSave,
  disabled = false,
  className,
  inputClassName,
  ariaLabel,
}: {
  value: string;
  onSave: (next: string) => void;
  disabled?: boolean;
  className?: string;
  inputClassName?: string;
  ariaLabel: string;
}) {
  const t = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saved, setSaved] = useState(value);
  const ref = useRef<HTMLInputElement | null>(null);

  // A rename landed from elsewhere while we were not editing: take it.
  // Adjusted during render rather than in an effect, which renders twice.
  if (!editing && saved !== value) {
    setSaved(value);
    setDraft(value);
  }
  useEffect(() => {
    if (editing) {
      ref.current?.focus();
      ref.current?.select();
    }
  }, [editing]);

  const commit = () => {
    const next = draft.trim();
    setEditing(false);
    if (next && next !== value) onSave(next);
  };

  if (editing) {
    // A <div>, not a <form>: this component is used inside other forms (the
    // template manager lives in the secret form), and nested forms submit the
    // wrong one. Enter and the tick button call commit() directly.
    return (
      <div className="flex items-center gap-2">
        <input
          ref={ref}
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              event.stopPropagation();
              commit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(value);
              setEditing(false);
            }
          }}
          aria-label={ariaLabel}
          maxLength={200}
          className={cn(
            'min-w-0 flex-1 rounded-lg bg-surface px-2 py-1 font-bold tracking-tight text-content',
            'ring-2 ring-accent/50 outline-none',
            inputClassName,
          )}
        />
        <button
          type="button"
          onMouseDown={(event) => event.preventDefault()}
          onClick={commit}
          aria-label={t('common.save')}
          className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent text-accent-content max-sm:h-11 max-sm:w-11"
        >
          <Check className="h-4 w-4" aria-hidden />
        </button>
        <button
          type="button"
          aria-label={t('common.cancel')}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => {
            setDraft(value);
            setEditing(false);
          }}
          className="flex h-9 w-9 items-center justify-center rounded-lg text-content-subtle hover:bg-surface-hover max-sm:h-11 max-sm:w-11"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className={cn('group/title flex min-w-0 items-center gap-2', className)}>
      <span className="min-w-0 break-words">{value}</span>
      {!disabled ? (
        <button
          type="button"
          onClick={() => setEditing(true)}
          aria-label={ariaLabel}
          title={t('common.rename')}
          className={cn(
            'shrink-0 rounded-md p-1.5 text-content-subtle transition-surface hover:bg-surface-hover hover:text-content',
            // Quiet on desktop until the heading is hovered; a phone has no hover.
            'opacity-0 group-hover/title:opacity-100 focus-visible:opacity-100',
            'max-sm:opacity-100 max-sm:p-2.5 touch:opacity-100 touch:p-2.5',
          )}
        >
          <Pencil className="h-4 w-4" aria-hidden />
        </button>
      ) : null}
    </div>
  );
}
